import React from 'react';
import { Grid } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import InfoField from './InfoField';

interface FieldConfig {
  id: string;
  name: string;
  displayName: string;
  fields: {
    ExpandedViewItemKey: { value: string };
    ExpandedViewItemLabel: { value: string };
  };
}

interface DemographicsVitalsProps {
  profile: Record<string, any>;
  fieldConfigs?: FieldConfig[];
  headerText?: string;
}

const DemographicsVitalsSection: React.FC<DemographicsVitalsProps> = ({
  profile,
  fieldConfigs,
  headerText,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  // Default field configurations if not provided from Sitecore
  const defaultFields = [
    { key: 'gender', label: 'Gender' },
    { key: 'demographics.age', label: 'Age' },
    { key: 'demographics.race', label: 'Race' },
    { key: 'demographics.ethnicity', label: 'Ethnicity' },
    { key: 'demographics.maritalStatus', label: 'Marital Status' },
    { key: 'vitals.height', label: 'Height' },
    { key: 'vitals.weight', label: 'Weight' },
    { key: 'vitals.bmi', label: 'BMI' },
    { key: 'vitals.heartRate', label: 'Heart Rate' },
    { key: 'vitals.respiration', label: 'Respiration' },
    { key: 'vitals.temperature', label: 'Temperature' },
    { key: 'vitals.bloodPressure', label: 'Blood Pressure' },
  ];

  // Use Sitecore field configs if available, otherwise use defaults
  const fields = fieldConfigs
    ? fieldConfigs.map((config) => ({
        key: config.fields.ExpandedViewItemKey.value,
        label: config.fields.ExpandedViewItemLabel.value,
      }))
    : defaultFields;

  // Helper to get nested value from object using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  return (
    <CustomAccordion
      title={headerText || 'Demographics & Vitals'}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mt: 1,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
      }}
      detailsSx={{ py: 1, px: 2 }}
    >
      <Grid container spacing={1.5}>
        {fields.map((field, index) => (
          <Grid item xs={12} sm={6} md={3} key={field.key || index}>
            <InfoField label={field.label} value={getNestedValue(profile, field.key)} />
          </Grid>
        ))}
      </Grid>
    </CustomAccordion>
  );
};

export default DemographicsVitalsSection;
